// Clerk auth bridge for the API client.
//
// The apiClient axios interceptor needs to attach an Authorization
// header on every outbound request. With Cognito the header came
// from Auth.ts:getAuthHeaders, which read the cached idToken from
// localStorage. With Clerk we can't use a React hook from inside
// axios, so we reach for the global window.Clerk session that
// ClerkProvider attaches at boot.
//
// `window.Clerk.session.getToken()` returns the active JWT (Clerk
// rotates tokens automatically; this call resolves to the freshest
// one). If no session exists (signed out / pre-auth bootstrap), we
// return an empty header set so the request proceeds without auth
// — the API Gateway authorizer will reject it with 401, which the
// existing 401 interceptor surface handles.

declare global {
  interface Window {
    Clerk?: {
      session?: {
        getToken: (options?: { template?: string }) => Promise<string | null>;
      };
    };
  }
}

export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await window.Clerk?.session?.getToken().catch(() => null);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Clerk handles token refresh internally — every getToken() returns a
// fresh-enough JWT. No explicit refresh call is needed; this function
// exists only to satisfy the apiClient interceptor's contract.
export const refreshToken = async (): Promise<boolean> => {
  const token = await window.Clerk?.session?.getToken().catch(() => null);
  return !!token;
};
